import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import { useMediaQuery } from 'react-responsive';
import L from 'leaflet';
import { css } from '@linaria/core';
import classNames from 'classnames';
import {
  Building,
  ChevronLeft,
  ChevronRight,
  Close,
  Delete,
  Discuss,
  FullScreen,
  Layers,
  Line,
  Minus,
  Plus,
  Polygon,
  Print,
  Radius,
  Rectangle,
  Target,
} from '../icons';
import { osTileThumb } from '../utils/mapTiles';
import {
  HEX_ANCHOR,
  HEX_SIZE,
  MARKER_BLUE,
  PIN_ANCHOR,
  PIN_SIZE,
  SHAPE_RED,
  buildHexSvg,
  buildPinSvg,
} from '../utils/mapMarkerStyles';

const FOCUS_RING = '0 0 0 3px #ffdd00';
const FACE_SHADOW = '0 4px 4px rgba(0, 0, 0, 0.25)';
const TAB_SHADOW = '4px 4px 4px rgba(0, 0, 0, 0.25)';

function useLeafletControlContainer(position, className = '') {
  const map = useMap();
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const div = L.DomUtil.create(
      'div',
      `leaflet-control map-control-element ${className}`,
    );
    L.DomEvent.disableClickPropagation(div);
    L.DomEvent.disableScrollPropagation(div);

    const Control = L.Control.extend({ onAdd: () => div });
    const control = new Control({ position });
    map.addControl(control);
    setContainer(div);

    return () => {
      map.removeControl(control);
      setContainer(null);
    };
  }, [map, position, className]);

  return container;
}

function MapControl({ position, className, children }) {
  const container = useLeafletControlContainer(position, className);
  return container ? createPortal(children, container) : null;
}

const buttonReset = css`
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
`;

const mapButton = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #ffffff;
  color: #1a1a1a;
  transition: background-color 150ms ease-in-out;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }

  &:hover {
    background-color: #f6f3ef;
  }

  &:focus {
    outline: none;
    box-shadow: ${FOCUS_RING};
    z-index: 1;
  }

  &[aria-pressed='true'] {
    background-color: #1a1a1a;
    color: #ffffff;
  }

  &:disabled {
    color: #b3b3b3;
    cursor: not-allowed;
  }

  &:disabled:hover {
    background-color: #ffffff;
  }
`;

const tooltipBubble = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  background-color: #ffffff;
  color: #1a1a1a;
  font-family: 'articulat-cf', Tahoma, Verdana;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.32px;
  padding: 7px 16px;
  border-radius: 4px;
  box-shadow: ${FACE_SHADOW};
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 120ms ease-in-out;
  z-index: 1000;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
  }
`;

const tooltipRight = css`
  left: calc(100% + 10px);

  &::after {
    right: 100%;
    border-right-color: #ffffff;
  }
`;

const tooltipLeft = css`
  right: calc(100% + 10px);

  &::after {
    left: 100%;
    border-left-color: #ffffff;
  }
`;

const tooltipVisible = css`
  opacity: 1;
  visibility: visible;
`;

const buttonStack = css`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  box-shadow: ${FACE_SHADOW};

  > button + button {
    border-top: 1px solid #e5dbd1;
  }
`;

const standaloneButton = css`
  box-shadow: ${FACE_SHADOW};
`;

const controlGroup = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MOBILE_MAP_QUERY = '(max-width: 768px)';

function useIsMobileMap() {
  return useMediaQuery({ query: MOBILE_MAP_QUERY });
}

function MapButton({
  label,
  onClick,
  active = false,
  children,
  className,
  tooltipSide = 'right',
  disabled = false,
}) {
  const [tooltipShown, setTooltipShown] = useState(false);
  const showTooltip = () => setTooltipShown(true);
  const hideTooltip = () => setTooltipShown(false);

  return (
    <button
      aria-label={label}
      aria-pressed={active}
      className={classNames(buttonReset, mapButton, className)}
      disabled={disabled}
      onBlur={hideTooltip}
      onClick={onClick}
      onFocus={showTooltip}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      type="button"
    >
      {children}
      {label && (
        <span
          aria-hidden="true"
          className={classNames(
            tooltipBubble,
            tooltipSide === 'left' ? tooltipLeft : tooltipRight,
            tooltipShown && tooltipVisible,
          )}
        >
          {label}
        </span>
      )}
    </button>
  );
}

const zoomStack = css`
  @media (max-width: 768px) {
    display: none;
  }
`;

function ZoomControl() {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const handler = () => setZoom(map.getZoom());
    map.on('zoomend', handler);
    return () => {
      map.off('zoomend', handler);
    };
  }, [map]);

  const atMax = zoom >= map.getMaxZoom();
  const atMin = zoom <= map.getMinZoom();

  return (
    <div className={classNames(buttonStack, zoomStack)}>
      <MapButton label="Zoom in" onClick={() => !atMax && map.zoomIn()}>
        <Plus />
      </MapButton>
      <MapButton label="Zoom out" onClick={() => !atMin && map.zoomOut()}>
        <Minus />
      </MapButton>
    </div>
  );
}

function FullScreenControl() {
  const map = useMap();
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    const handler = () => {
      setIsFull(Boolean(document.fullscreenElement));
      // Entering/leaving full screen resizes the container, so let Leaflet
      // recompute its size and load tiles for the new viewport.
      setTimeout(() => map.invalidateSize(), 100);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [map]);

  const toggle = () => {
    // Full-screen the outer container, not Leaflet's element, so the scale,
    // copyright and result dialog (its siblings) stay visible.
    const el = map.getContainer().parentElement || map.getContainer();
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <MapButton
      active={isFull}
      className={standaloneButton}
      label={isFull ? 'Exit full screen' : 'Full screen'}
      onClick={toggle}
    >
      <FullScreen />
    </MapButton>
  );
}

function DefaultViewControl({ center, zoom }) {
  const map = useMap();
  if (!center || zoom == null) return null;

  return (
    <MapButton
      className={standaloneButton}
      label="Reset map"
      onClick={() => map.setView(center, zoom, { animate: true })}
    >
      <Building />
    </MapButton>
  );
}

function PrintControl({ onPrint, busy }) {
  const map = useMap();
  if (!onPrint) return null;

  return (
    <MapButton
      className={standaloneButton}
      disabled={busy}
      label="Print map"
      onClick={() => onPrint(map)}
    >
      <Print />
    </MapButton>
  );
}

const myLocationButton = css`
  background-color: #4786ed;
  color: #ffffff;
  border-radius: 50%;
  box-shadow: ${FACE_SHADOW};

  &:hover {
    background-color: #2c6cd3;
  }

  &[aria-pressed='true'] {
    background-color: #2c6cd3;
  }
`;

function MyLocationControl({ onLocate }) {
  const map = useMap();
  const [locating, setLocating] = useState(false);

  const handleClick = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude, longitude } = pos.coords;
        map.setView([latitude, longitude], 13, { animate: true });
        if (onLocate) onLocate({ latitude, longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <MapButton
      active={locating}
      className={myLocationButton}
      label="My location"
      onClick={handleClick}
    >
      <Target />
    </MapButton>
  );
}

function LeftControlStack({ defaultCenter, defaultZoom, onPrint, isPrinting }) {
  const isMobile = useIsMobileMap();

  return (
    <MapControl position="topleft">
      <div className={controlGroup}>
        <FullScreenControl />
        <ZoomControl />
        {!isMobile && (
          <>
            <DefaultViewControl center={defaultCenter} zoom={defaultZoom} />
            <PrintControl busy={isPrinting} onPrint={onPrint} />
          </>
        )}
        <MyLocationControl />
      </div>
    </MapControl>
  );
}

const drawStack = css`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  box-shadow: ${FACE_SHADOW};

  > button + button {
    position: relative;
  }

  > button + button::before {
    content: '';
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    height: 1px;
    background-color: #e5dbd1;
  }
`;

const BASEMAPS = [
  {
    id: 'os-light',
    name: 'OS Light',
    layer: 'Light_3857',
    type: 'os',
    thumb: osTileThumb('Light_3857'),
    description: 'Crown copyright [and database rights] 2026. OS AC0000815036.',
  },
  {
    id: 'os-road',
    name: 'OS Road',
    layer: 'Road_3857',
    type: 'os',
    thumb: osTileThumb('Road_3857'),
    description: 'Crown copyright [and database rights] 2026. OS AC0000815036.',
  },
  {
    id: 'os-outdoor',
    name: 'OS Outdoor',
    layer: 'Outdoor_3857',
    type: 'os',
    thumb: osTileThumb('Outdoor_3857'),
    description: 'Crown copyright [and database rights] 2026. OS AC0000815036.',
  },
  {
    id: 'aerial',
    name: 'Aerial',
    layer: 'APGB_Latest_UK_250mm',
    type: 'aerial',
    thumb:
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/7/41/63',
    description:
      'Bluesky International Limited and Getmapping Limited 1999-2020. Bluesky International Limited 2021 and onwards.',
  },
];

const basemapButton = css`
  background-color: #ffffff;
  box-shadow: ${FACE_SHADOW};
`;

const basemapCaret = css`
  position: absolute;
  top: 50%;
  right: 38px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 5.5px solid transparent;
  border-bottom: 5.5px solid transparent;
  border-right: 8px solid #1a1a1a;
  z-index: 501;
  pointer-events: none;
`;

const basemapPanel = css`
  position: absolute;
  top: 0;
  right: 56px;
  width: 224px;
  max-width: calc(100vw - 100px);
  background-color: #ffffff;
  box-shadow: ${FACE_SHADOW};
  padding: 0;
  display: flex;
  flex-direction: column;
  font-family: 'articulat-cf', Tahoma, Verdana;
  z-index: 500;

  .basemap-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    border-bottom: 1px solid #e5dbd1;
  }

  .basemap-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    letter-spacing: 0.36px;
    color: #1a1a1a;
  }

  .basemap-close {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1a1a;

    svg {
      width: 16px;
      height: 16px;
    }

    &:focus {
      outline: none;
      box-shadow: ${FOCUS_RING};
    }
  }
`;

const basemapOption = css`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 10px;
  width: 100%;
  height: 70px;
  text-align: left;
  font-family: inherit;
  box-sizing: border-box;

  &:not(:last-child) {
    border-bottom: 1px solid #e5dbd1;
  }

  &:hover {
    background-color: #f9f6f4;
  }

  &:focus {
    outline: none;
    box-shadow: inset ${FOCUS_RING};
  }

  .basemap-thumb {
    width: 50px;
    height: 50px;
    border-radius: 4px;
    background-color: #e5dbd1;
    background-size: cover;
    background-position: center;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .basemap-name {
    flex: 1;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    color: #1a1a1a;
  }

  &[aria-pressed='true'] {
    background-color: #f9f6f4;

    .basemap-thumb {
      border: 2px solid #ffffff;
    }

    .basemap-name {
      font-weight: 600;
    }
  }
`;

function BasemapControl({ activeBasemap, onSelect, isOpen, onOpenChange }) {
  const controlled = isOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? isOpen : internalOpen;
  const setOpen = next => {
    const value = typeof next === 'function' ? next(open) : next;
    if (controlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <MapButton
        active={open}
        className={basemapButton}
        label="Basemaps"
        onClick={() => setOpen(prev => !prev)}
        tooltipSide="left"
      >
        <Layers />
      </MapButton>
      {open && <span aria-hidden="true" className={basemapCaret} />}
      {open && (
        <div className={basemapPanel}>
          <div className="basemap-header">
            <span className="basemap-title">Basemap</span>
            <button
              aria-label="Close basemaps"
              className={classNames(buttonReset, 'basemap-close')}
              onClick={() => setOpen(false)}
              type="button"
            >
              <Close />
            </button>
          </div>
          {BASEMAPS.map(b => (
            <button
              aria-pressed={activeBasemap === b.id}
              className={classNames(buttonReset, basemapOption)}
              key={b.id}
              onClick={() => {
                onSelect(b.id);
                setOpen(false);
              }}
              type="button"
            >
              <span
                className="basemap-thumb"
                style={
                  b.thumb ? { backgroundImage: `url(${b.thumb})` } : undefined
                }
              />
              <span className="basemap-name">{b.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const DRAW_TOOLS = [
  { id: 'line', label: 'Draw a line with buffer', Icon: Line },
  { id: 'polygon', label: 'Draw a polygon', Icon: Polygon },
  { id: 'rectangle', label: 'Draw a rectangle', Icon: Rectangle },
  { id: 'circle', label: 'Draw a circle', Icon: Radius },
];

function DrawToolsControl({
  activeTool,
  hasDrawnShape,
  onToolSelect,
  onClear,
}) {
  return (
    <>
      <div className={drawStack}>
        {DRAW_TOOLS.map(({ id, label, Icon }) => (
          <MapButton
            active={activeTool === id}
            disabled={hasDrawnShape}
            key={id}
            label={
              hasDrawnShape ? 'Remove the drawn shape to draw again' : label
            }
            onClick={() => onToolSelect(id === activeTool ? null : id)}
            tooltipSide="left"
          >
            <Icon />
          </MapButton>
        ))}
      </div>
      {hasDrawnShape && <DeleteLayersControl onClear={onClear} />}
    </>
  );
}

function DeleteLayersControl({ onClear }) {
  return (
    <MapButton
      className={standaloneButton}
      label="Remove drawn shape"
      onClick={onClear}
      tooltipSide="left"
    >
      <Delete />
    </MapButton>
  );
}

function RightControlStack({
  activeBasemap,
  onBasemapSelect,
  basemapOpen,
  onBasemapOpenChange,
  activeTool,
  hasDrawnShape,
  onToolSelect,
  onClear,
}) {
  const isMobile = useIsMobileMap();

  return (
    <MapControl position="topright">
      <div className={controlGroup}>
        <BasemapControl
          activeBasemap={activeBasemap}
          isOpen={basemapOpen}
          onOpenChange={onBasemapOpenChange}
          onSelect={onBasemapSelect}
        />
        {!isMobile && (
          <DrawToolsControl
            activeTool={activeTool}
            hasDrawnShape={hasDrawnShape}
            onClear={onClear}
            onToolSelect={onToolSelect}
          />
        )}
      </div>
    </MapControl>
  );
}

function getRoundNum(num) {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1);
  let d = num / pow10;
  d = d >= 10 ? 10 : d >= 5 ? 5 : d >= 3 ? 3 : d >= 2 ? 2 : 1;
  return pow10 * d;
}

const SCALE_MAX_WIDTH = 104;

function ScaleComputer({ metric, onChange }) {
  const map = useMap();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const update = () => {
      const size = map.getSize();
      if (!size.x) return;
      const y = size.y / 2;
      const maxMeters = map
        .containerPointToLatLng([0, y])
        .distanceTo(map.containerPointToLatLng([SCALE_MAX_WIDTH, y]));
      if (!maxMeters) return;

      // mapWidth travels with the reading so the print renderer can rescale the
      // bar from screen pixels to the printed map's width.
      if (metric) {
        const meters = getRoundNum(maxMeters);
        onChangeRef.current({
          label: meters < 1000 ? `${meters} m` : `${meters / 1000} km`,
          width: Math.round(SCALE_MAX_WIDTH * (meters / maxMeters)),
          mapWidth: size.x,
        });
      } else {
        const feet = maxMeters * 3.2808399;
        if (feet > 5280) {
          const miles = getRoundNum(feet / 5280);
          onChangeRef.current({
            label: `${miles} mi`,
            width: Math.round(SCALE_MAX_WIDTH * ((miles * 5280) / feet)),
            mapWidth: size.x,
          });
        } else {
          const ft = getRoundNum(feet);
          onChangeRef.current({
            label: `${ft} ft`,
            width: Math.round(SCALE_MAX_WIDTH * (ft / feet)),
            mapWidth: size.x,
          });
        }
      }
    };

    update();
    map.on('zoomend', update);
    map.on('moveend', update);
    map.on('resize', update);
    return () => {
      map.off('zoomend', update);
      map.off('moveend', update);
      map.off('resize', update);
    };
  }, [map, metric]);

  return null;
}

const scaleControl = css`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 450;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 30px;
  padding: 0 8px;
  box-sizing: border-box;
  background-color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  user-select: none;
  font-family: 'articulat-cf', Tahoma, Verdana;

  @media (max-width: 768px) {
    display: none;
  }

  .scale-label {
    font-size: 12px;
    font-weight: 600;
    line-height: 26px;
    letter-spacing: 0.24px;
    color: #1a1a1a;
    white-space: nowrap;
  }

  .scale-bar {
    height: 7px;
    border-left: 2px solid #000000;
    border-right: 2px solid #000000;
    border-bottom: 2px solid #000000;
    box-sizing: border-box;
  }

  &:focus {
    outline: none;
    box-shadow: ${FOCUS_RING};
  }
`;

function MapScale({ label, width, metric, onToggle }) {
  if (!label) return null;
  return (
    <div
      className={scaleControl}
      onClick={onToggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      role="button"
      tabIndex={0}
      title={metric ? 'Switch to miles' : 'Switch to kilometres'}
    >
      <span className="scale-label">{label}</span>
      <span className="scale-bar" style={{ width: `${width}px` }} />
    </div>
  );
}

const copyrightControl = css`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 450;
  height: 30px;
  display: flex;
  align-items: center;
  padding: 2px 8px;
  box-sizing: border-box;
  background-color: rgba(255, 255, 255, 0.8);
  font-family: 'articulat-cf', Tahoma, Verdana;
  font-size: 12px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.24px;
  color: #000000;
  white-space: nowrap;
  pointer-events: none;

  @media (max-width: 768px) {
    left: 0;
    height: auto;
    white-space: normal;
    text-align: left;
  }
`;

// The notice is a fixed 30px strip on desktop but wraps to two or three lines on
// narrow screens, so its height is measured and published: the result dialog
// positions itself 10px above whatever it currently occupies.
function MapCopyright({ text, onHeightChange }) {
  const ref = useRef(null);
  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      onHeightChangeRef.current?.(0);
      return undefined;
    }

    const report = () => onHeightChangeRef.current?.(el.offsetHeight);
    report();

    const observer = new ResizeObserver(report);
    observer.observe(el);

    return () => {
      observer.disconnect();
      onHeightChangeRef.current?.(0);
    };
  }, [text]);

  if (!text) return null;

  return <div className={copyrightControl} ref={ref}>{`© ${text}`}</div>;
}

const sidebarTab = css`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 35px;
  height: 80px;
  background-color: #ffffff;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${TAB_SHADOW};
  z-index: 500;
  cursor: pointer;
  transition:
    background-color 150ms ease-in-out,
    color 150ms ease-in-out;

  @media (max-width: 1079px) {
    display: none;
  }

  svg {
    width: 13px;
    height: 20px;
  }

  &:hover {
    background-color: #1a1a1a;
    color: #ffffff;
  }

  &:focus {
    outline: none;
    box-shadow: ${FOCUS_RING}, ${TAB_SHADOW};
  }
`;

function SidebarCollapseTab({ isOpen, onToggle }) {
  const [tooltipShown, setTooltipShown] = useState(false);
  const label = isOpen ? 'Hide filters to enlarge map' : 'Show filters';

  return (
    <button
      aria-label={label}
      className={classNames(buttonReset, sidebarTab)}
      data-state={isOpen ? 'open' : 'closed'}
      onBlur={() => setTooltipShown(false)}
      onClick={onToggle}
      onFocus={() => setTooltipShown(true)}
      onMouseEnter={() => setTooltipShown(true)}
      onMouseLeave={() => setTooltipShown(false)}
      type="button"
    >
      {isOpen ? <ChevronLeft /> : <ChevronRight />}
      <span
        aria-hidden="true"
        className={classNames(
          tooltipBubble,
          tooltipRight,
          tooltipShown && tooltipVisible,
        )}
      >
        {label}
      </span>
    </button>
  );
}

const toolPanel = css`
  position: absolute;
  top: 10px;
  right: 60px;
  width: 302px;
  max-width: calc(100% - 120px);
  font-family: 'articulat-cf', Tahoma, Verdana;
  background-color: #ffffff;
  box-shadow: ${FACE_SHADOW};
  padding: 0;
  display: flex;
  flex-direction: column;
  z-index: 500;
  pointer-events: auto;

  .tool-pointer {
    position: absolute;
    right: -8px;
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 8px solid #ffffff;
    pointer-events: none;
  }

  .tool-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px;
    border-bottom: 1px solid #e5dbd1;
  }

  .tool-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    letter-spacing: 0.36px;
    color: #1a1a1a;
    margin: 0;
    flex: 1;
  }

  .tool-close {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1a1a;
    flex-shrink: 0;

    svg {
      width: 14px;
      height: 14px;
    }

    &:focus {
      outline: none;
      box-shadow: ${FOCUS_RING};
    }
  }

  .tool-buffer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border-bottom: 1px solid #e5dbd1;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    color: #1a1a1a;

    label {
      flex: 1;
    }

    input {
      width: 61px;
      height: 40px;
      border: 1px solid #1a1a1a;
      border-radius: 5px;
      padding: 0 10px;
      font: inherit;
      text-align: left;
      box-sizing: border-box;
      appearance: textfield;
      -moz-appearance: textfield;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input:focus {
      outline: none;
      box-shadow: ${FOCUS_RING};
    }

    .unit {
      text-align: left;
    }
  }

  .tool-instructions {
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    color: #1a1a1a;
    margin: 0;
    padding: 10px;
  }

  .tool-actions {
    display: flex;
    gap: 16px;
    padding: 0 10px 10px;
  }

  .tool-action {
    font-size: 16px;
    font-weight: 600;
    line-height: 26px;
    letter-spacing: 0.32px;
    color: #1a1a1a;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    background: none;
    border: 0;
    padding: 0;

    &[disabled] {
      opacity: 0.4;
      cursor: not-allowed;
      text-decoration: none;
    }

    &:focus {
      outline: none;
      box-shadow: ${FOCUS_RING};
    }
  }
`;

const TOOL_COPY = {
  line: {
    title: 'Line buffer',
    instructions: 'Click to start drawing line',
    withBuffer: true,
    withActions: true,
  },
  polygon: {
    title: 'Polygon',
    instructions: 'Click to add points. Click first point to finish.',
    withBuffer: false,
    withActions: true,
  },
  rectangle: {
    title: 'Rectangle',
    instructions: 'Click and drag to draw a rectangle.',
    withBuffer: false,
    withActions: false,
  },
  circle: {
    title: 'Circle',
    instructions: 'Click and drag to draw a circle.',
    withBuffer: false,
    withActions: false,
  },
};

function ToolPanel({
  tool,
  bufferRadius,
  onBufferChange,
  canDeleteLastPoint,
  canFinish,
  onFinish,
  onDeleteLastPoint,
  onClose,
  topOffset,
  pointerOffset,
}) {
  if (!tool || !TOOL_COPY[tool]) return null;
  const copy = TOOL_COPY[tool];

  return (
    <div
      className={toolPanel}
      style={topOffset != null ? { top: `${topOffset}px` } : undefined}
    >
      {pointerOffset != null && (
        <span
          aria-hidden="true"
          className="tool-pointer"
          style={{ top: `${pointerOffset}px` }}
        />
      )}
      <div className="tool-header">
        <h3 className="tool-title">{copy.title}</h3>
        <button
          aria-label="Close"
          className={classNames(buttonReset, 'tool-close')}
          onClick={onClose}
          type="button"
        >
          <Close />
        </button>
      </div>
      {copy.withBuffer && (
        <div className="tool-buffer">
          <label htmlFor="line-buffer-input">Set your buffer</label>
          <input
            id="line-buffer-input"
            max="50000"
            min="50"
            onChange={e => onBufferChange(Number(e.target.value))}
            step="50"
            type="number"
            value={bufferRadius}
          />
          <span className="unit">m</span>
        </div>
      )}
      <p className="tool-instructions">{copy.instructions}</p>
      {copy.withActions && (
        <div className="tool-actions">
          <button
            className="tool-action"
            disabled={!canFinish}
            onClick={onFinish}
            type="button"
          >
            Finish
          </button>
          <button
            className="tool-action"
            disabled={!canDeleteLastPoint}
            onClick={onDeleteLastPoint}
            type="button"
          >
            Delete last point
          </button>
        </div>
      )}
    </div>
  );
}

const drawTooltip = css`
  position: absolute;
  background-color: #1a1a1a;
  color: #ffffff;
  padding: 5px 10px;
  font-family: 'articulat-cf', Tahoma, Verdana;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.32px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 600;
  transform: translate(14px, -50%);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -8px;
    transform: translateY(-50%);
    border-top: 5.5px solid transparent;
    border-bottom: 5.5px solid transparent;
    border-right: 8px solid #1a1a1a;
  }

  .draw-tooltip-measurement {
    display: block;
    font-family: 'auger-mono', monospace;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: 0.16px;
    color: #66deb5;
  }
`;

const CURSOR_TOOLTIP_COPY = {
  line: {
    start: 'Click to start drawing line',
    midway: 'Click to continue. Touch last point to finish line',
    ready: 'Touch last point to finish line',
  },
  polygon: {
    start: 'Click to add points',
    midway: 'Click to add points. Click first point to finish.',
    ready: 'Click first point to finish',
  },
  rectangle: {
    start: 'Click and drag to draw a rectangle',
  },
  circle: {
    start: 'Click and drag to draw a circle',
  },
};

function DrawCursorTooltip({ activeTool, pointCount, measurement }) {
  const map = useMap();
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!activeTool || !CURSOR_TOOLTIP_COPY[activeTool]) {
      setPos(null);
      return undefined;
    }
    const onMove = e => {
      const point = map.latLngToContainerPoint(e.latlng);
      setPos({ x: point.x, y: point.y });
    };
    const onOut = () => setPos(null);
    map.on('mousemove', onMove);
    map.on('mouseout', onOut);
    return () => {
      map.off('mousemove', onMove);
      map.off('mouseout', onOut);
    };
  }, [map, activeTool]);

  if (!activeTool || !pos || !CURSOR_TOOLTIP_COPY[activeTool]) return null;

  const copy = CURSOR_TOOLTIP_COPY[activeTool];
  let text;
  if (pointCount === 0 || !copy.midway) {
    text = copy.start;
  } else if (
    (activeTool === 'line' && pointCount >= 2) ||
    (activeTool === 'polygon' && pointCount >= 3)
  ) {
    text = copy.ready;
  } else {
    text = copy.midway;
  }

  return createPortal(
    <div className={drawTooltip} style={{ left: pos.x, top: pos.y }}>
      {measurement && (
        <span className="draw-tooltip-measurement">{measurement}</span>
      )}
      {text}
    </div>,
    map.getContainer(),
  );
}

function createPinIcon(selected = false) {
  return L.divIcon({
    html: buildPinSvg(selected),
    className: 'he-map-pin',
    iconSize: PIN_SIZE,
    iconAnchor: PIN_ANCHOR,
    popupAnchor: [0, -PIN_SIZE[1]],
  });
}

function createHexIcon(selected = false) {
  return L.divIcon({
    html: buildHexSvg(selected),
    className: 'he-map-hex',
    iconSize: HEX_SIZE,
    iconAnchor: HEX_ANCHOR,
  });
}

const resultPopup = css`
  position: absolute;
  bottom: 40px;
  left: 10px;
  right: 10px;
  max-width: 856px;
  min-height: 156px;
  box-sizing: border-box;
  background-color: #ffffff;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  z-index: 500;
  pointer-events: auto;
  font-family: 'articulat-cf', Tahoma, Verdana;

  @media (max-width: 799px) {
    bottom: 10px;
    right: 10px;
    max-width: none;
    min-height: 0;
    flex-direction: column;
    background-color: transparent;
    box-shadow: none;
  }

  .popup-thumb {
    width: 220px;
    height: 156px;
    object-fit: cover;
    background-color: #e5dbd1;
    flex-shrink: 0;
    align-self: flex-start;
    display: block;

    @media (max-width: 799px) {
      width: 118px;
      height: 84px;
      border: 2px solid #ffffff;
      box-sizing: border-box;
    }
  }

  .popup-body {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 54px 10px 20px;

    @media (max-width: 799px) {
      padding: 10px;
      background-color: #ffffff;
      box-shadow: 0 4px 4px rgba(0, 0, 0, 0.2);
    }
  }

  .popup-category {
    font-family: 'auger-mono', monospace;
    font-size: 16px;
    font-weight: 400;
    line-height: 28px;
    letter-spacing: 0.16px;
    color: #1a1a1a;
    margin: 0;
  }

  .popup-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 26px;
    letter-spacing: 0.32px;
    color: #1a1a1a;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;

    a {
      color: inherit;
      font-weight: 600;
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 2px;
    }
  }

  .popup-entry-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    color: #1a1a1a;
  }

  .popup-entries {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    color: #1a1a1a;
  }

  .popup-entry {
    margin: 0;

    .popup-entry-label {
      font-weight: 600;
    }
  }

  .popup-discuss {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #1a1a1a;

    svg {
      width: 21px;
      height: 21px;
      display: block;
    }
  }

  .popup-close {
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f9f6f4;
    border: 0;
    cursor: pointer;
    color: #1a1a1a;

    @media (max-width: 799px) {
      background-color: transparent;
    }

    svg {
      width: 14px;
      height: 14px;
      display: block;
    }

    &:hover {
      background-color: #f0e9e2;
    }

    &:focus {
      outline: none;
      box-shadow: inset ${FOCUS_RING};
    }
  }
`;

// The accent border marks what kind of feature is selected.
const popupAccentPoint = css`
  border-left: 10px solid ${MARKER_BLUE};

  @media (max-width: 799px) {
    border-left: 0;

    .popup-body {
      border-left: 10px solid ${MARKER_BLUE};
    }
  }
`;

const popupAccentShape = css`
  border-left: 10px solid ${SHAPE_RED};

  @media (max-width: 799px) {
    border-left: 0;

    .popup-body {
      border-left: 10px solid ${SHAPE_RED};
    }
  }
`;

function ResultPopup({ result, onClose, variant = 'point', bottomOffset }) {
  if (!result) return null;

  const { title, url, image, category, listEntry, comments, entries } = result;

  return (
    <div
      aria-label={title}
      className={classNames(
        resultPopup,
        variant === 'shape' ? popupAccentShape : popupAccentPoint,
      )}
      role="dialog"
      style={bottomOffset != null ? { bottom: `${bottomOffset}px` } : undefined}
    >
      {/* Key on the src so switching pins remounts the image instead of showing
          the previous one until the new src loads. */}
      {image && <img alt="" className="popup-thumb" key={image} src={image} />}
      <div className="popup-body">
        <button
          aria-label="Close"
          className="popup-close"
          onClick={onClose}
          type="button"
        >
          <Close />
        </button>
        {category && <p className="popup-category">{category}</p>}
        <h3 className="popup-title">
          {url ? <a href={url}>{title}</a> : title}
        </h3>
        {entries && entries.length > 0 && (
          <div className="popup-entries">
            {entries.map(entry => (
              <p className="popup-entry" key={entry.label}>
                <span className="popup-entry-label">{entry.label}:</span>{' '}
                {entry.value}
              </p>
            ))}
          </div>
        )}
        {(listEntry || comments != null) && (
          <div className="popup-entry-row">
            {listEntry && (
              <p className="popup-entry">
                <span className="popup-entry-label">List Entry No:</span>{' '}
                {listEntry}
              </p>
            )}
            {comments != null && (
              <span className="popup-discuss">
                <Discuss />
                {comments}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export {
  MapControl,
  MapButton,
  LeftControlStack,
  RightControlStack,
  ScaleComputer,
  MapScale,
  MapCopyright,
  SidebarCollapseTab,
  ToolPanel,
  ResultPopup,
  DrawCursorTooltip,
  BASEMAPS,
  createHexIcon,
  createPinIcon,
};
